import React, { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate, fmtDateOnly, CATEGORIA_LABEL } from "@/lib/api";
import { SectionTitle, Badge } from "@/components/Brutal";
import { ExportButtons } from "@/components/ExportButtons";
import { useAuth } from "@/contexts/AuthContext";
import AssembleiaReport from "@/pages/AssembleiaReport";
import {
  BarChart3, TrendingUp, Trees, Package, Building2, HardHat,
  Network, CalendarRange, AlertOctagon, ArrowLeftRight, Award,
} from "lucide-react";

const TABS = [
  { id: "assembleia", label: "Assembleia", icon: Award, highlight: true },
  { id: "movimentacoes", label: "Movimentações", icon: ArrowLeftRight },
  { id: "estoque-atual", label: "Estoque Atual", icon: Package },
  { id: "mensal", label: "Resumo Mensal", icon: CalendarRange },
  { id: "requisicoes", label: "Requisições", icon: Network },
  { id: "compras", label: "Compras", icon: Building2 },
  { id: "estoque", label: "Análise Estoque", icon: TrendingUp },
  { id: "epis", label: "EPIs", icon: HardHat },
  { id: "ca-epi", label: "CA dos EPIs", icon: AlertOctagon },
  { id: "consumo", label: "Consumo", icon: Network },
];

const Bar = ({ value, max, color = "#FF4F00" }) => (
  <div className="h-3 bg-[#EEE] border border-[#111]">
    <div className="h-full" style={{ width: `${Math.min(100, (value / max) * 100)}%`, background: color }} />
  </div>
);

export default function Reports() {
  const { hasRole } = useAuth();
  const canSee = (r) => hasRole(r);
  const [tab, setTab] = useState("assembleia");
  const [data, setData] = useState({});
  const [periodStart, setStart] = useState("");
  const [periodEnd, setEnd] = useState("");
  const [reqStatus, setReqStatus] = useState("");
  const [reqCategoria, setReqCategoria] = useState("");
  const [stockOnlyLow, setStockOnlyLow] = useState(false);

  const loadTab = async (which) => {
    try {
      if (which === "assembleia") {
        return; // AssembleiaReport carrega seus próprios dados
      }
      if (which === "estoque") {
        const [top, cat, exp] = await Promise.all([
          api.get("/reports/top-products"),
          api.get("/reports/by-category"),
          api.get("/reports/expiring-products?days=60"),
        ]);
        setData((d) => ({ ...d, top: top.data, byCat: cat.data, expiring: exp.data }));
      } else if (which === "estoque-atual") {
        const q = stockOnlyLow ? "?only_low=true" : "";
        const { data: r } = await api.get(`/reports/stock-current${q}`);
        setData((d) => ({ ...d, stockCurrent: r }));
      } else if (which === "mensal") {
        const { data: r } = await api.get(`/reports/monthly-summary?months=12`);
        setData((d) => ({ ...d, monthlySummary: r }));
      } else if (which === "requisicoes") {
        const q = [];
        if (periodStart) q.push(`start=${periodStart}`);
        if (periodEnd) q.push(`end=${periodEnd}`);
        if (reqStatus) q.push(`status=${reqStatus}`);
        if (reqCategoria) q.push(`categoria=${reqCategoria}`);
        const { data: r } = await api.get(`/reports/requisitions-report${q.length ? "?" + q.join("&") : ""}`);
        setData((d) => ({ ...d, reqsReport: r }));
      } else if (which === "compras") {
        const calls = [api.get("/reports/monthly-purchases")];
        if (canSee("almoxarife", "presidencia")) {
          calls.push(api.get("/reports/by-supplier"));
          calls.push(api.get("/reports/by-community"));
        }
        const res = await Promise.all(calls);
        setData((d) => ({ ...d, monthly: res[0].data, bySupplier: res[1]?.data || [], byCommunity: res[2]?.data || [] }));
      } else if (which === "epis") {
        const [r, all] = await Promise.all([
          api.get("/reports/epi-by-collaborator"),
          api.get("/epi-deliveries"),
        ]);
        setData((d) => ({ ...d, epiByCollab: r.data, epiAll: all.data }));
      } else if (which === "ca-epi") {
        const { data: r } = await api.get("/reports/epi-ca-status?days=30");
        setData((d) => ({ ...d, caEpi: r }));
      } else if (which === "consumo") {
        const { data: r } = await api.get("/reports/consumption-by-sector");
        setData((d) => ({ ...d, bySector: r }));
      } else if (which === "movimentacoes") {
        const q = [];
        if (periodStart) q.push(`start=${periodStart}`);
        if (periodEnd) q.push(`end=${periodEnd}`);
        const { data: r } = await api.get(`/reports/movements-period${q.length ? "?" + q.join("&") : ""}`);
        setData((d) => ({ ...d, movPeriod: r }));
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadTab(tab); /* eslint-disable-next-line */ }, [tab]);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="assembleia-noprint">
        <SectionTitle>Relatórios · <BarChart3 className="inline w-5 h-5 -mt-1" /></SectionTitle>
      </div>

      {/* Export dos datasets brutos */}
      {tab !== "assembleia" && (
        <div className="brutal-card-sm p-3 flex flex-wrap items-center gap-3 bg-[#F4F8F5]">
          <span className="font-mono-data text-[10px] uppercase tracking-widest text-[#3D6B57]">Exportar dataset:</span>
          <ExportButtons report="products" />
          <ExportButtons report="movements" params={{ start: periodStart, end: periodEnd }} />
          <ExportButtons report="epi-deliveries" />
          <ExportButtons report="supplier-purchases" />
          <ExportButtons report="community-purchases" />
          <ExportButtons report="requisitions" />
          <ExportButtons report="visitor-tickets" />
          <ExportButtons report="returns" />
          <ExportButtons report="cooperados" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-0 border-2 border-[#111] bg-white assembleia-noprint">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.id;
          const highlightIdle = t.highlight && !isActive;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 min-w-[120px] px-4 py-3 font-display font-bold uppercase text-[11px] tracking-widest inline-flex items-center justify-center gap-2 border-r-2 border-[#111] last:border-r-0 relative ${
                isActive ? "bg-[#FF4F00] text-white" :
                highlightIdle ? "bg-gradient-to-br from-[#1E5D3F] to-[#0B2E1F] text-white hover:from-[#164C33] hover:to-[#082418]" :
                "bg-white text-[#111] hover:bg-[#F0EFEA]"
              }`}
              data-testid={`tab-${t.id}`}>
              <Icon className="w-4 h-4" />{t.label}
              {t.highlight && !isActive && (
                <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#D4A017] animate-pulse" />
              )}
            </button>
          );
        })}
      </div>

      {tab === "assembleia" && <AssembleiaReport />}
      {tab === "estoque" && <Estoque data={data} />}
      {tab === "estoque-atual" && <EstoqueAtual data={data.stockCurrent} onlyLow={stockOnlyLow} setOnlyLow={setStockOnlyLow} reload={() => loadTab("estoque-atual")} />}
      {tab === "mensal" && <ResumoMensal data={data.monthlySummary} />}
      {tab === "requisicoes" && <RequisicoesReport data={data.reqsReport} periodStart={periodStart} periodEnd={periodEnd} setStart={setStart} setEnd={setEnd} reqStatus={reqStatus} setReqStatus={setReqStatus} reqCategoria={reqCategoria} setReqCategoria={setReqCategoria} reload={() => loadTab("requisicoes")} />}
      {tab === "compras" && <Compras data={data} canSee={canSee} />}
      {tab === "epis" && <Epis data={data} />}
      {tab === "ca-epi" && <CAEpi data={data.caEpi} />}
      {tab === "consumo" && <Consumo data={data} />}
      {tab === "movimentacoes" && <Movs data={data} periodStart={periodStart} periodEnd={periodEnd} setStart={setStart} setEnd={setEnd} reload={() => loadTab("movimentacoes")} />}
    </div>
  );
}

function Card({ title, icon: Icon, children, color = "#FF4F00" }) {
  return (
    <div className="brutal-card p-6">
      <div className="flex items-center gap-2 mb-4 pb-2 border-b-2 border-[#111]">
        {Icon && <Icon className="w-4 h-4" style={{ color }} />}
        <h3 className="font-display font-bold uppercase text-sm tracking-wider">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Estoque({ data }) {
  const top = data.top || [], byCat = data.byCat || [], exp = data.expiring || [];
  const maxTop = Math.max(1, ...top.map((p) => p.total));
  const maxCat = Math.max(1, ...byCat.map((c) => c.value));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card title="Produtos Mais Saídos" icon={TrendingUp}>
        {top.length === 0 ? <div className="text-center text-[#777] font-mono-data text-sm py-8">Sem dados.</div>
          : <div className="space-y-3">
            {top.map((p, i) => (
              <div key={i}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono-data text-xs text-[#777] w-6">#{i + 1}</span>
                    <span className="font-mono-data text-xs">{p.codigo}</span>
                    <span className="truncate">{p.nome}</span>
                  </div>
                  <span className="font-mono-data font-bold">{p.total}</span>
                </div>
                <Bar value={p.total} max={maxTop} />
              </div>
            ))}
          </div>}
      </Card>
      <Card title="Estoque por Categoria" icon={Package} color="#2D6A4F">
        {byCat.length === 0 ? <div className="text-center text-[#777] font-mono-data text-sm py-8">Sem produtos.</div>
          : <div className="space-y-3">
            {byCat.map((c, i) => (
              <div key={i}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <Badge variant="green">{CATEGORIA_LABEL[c.categoria] || c.categoria}</Badge>
                  <div className="flex items-center gap-3">
                    <span className="font-mono-data text-xs text-[#555]">{c.count} itens</span>
                    <span className="font-mono-data font-bold">{fmtBRL(c.value)}</span>
                  </div>
                </div>
                <Bar value={c.value} max={maxCat} color="#2D6A4F" />
              </div>
            ))}
          </div>}
      </Card>
      <div className="lg:col-span-2">
        <Card title="Produtos Vencendo (próximos 60 dias)" icon={AlertOctagon} color="#E60000">
          {exp.length === 0 ? <div className="text-center text-[#777] font-mono-data text-sm py-8">Nenhum produto a vencer.</div>
            : <div className="overflow-x-auto"><table className="brutal-table">
              <thead><tr><th>Código</th><th>Produto</th><th>Categoria</th><th>Lote</th><th>Validade</th><th className="text-right">Estoque</th><th>Status</th></tr></thead>
              <tbody>{exp.map((p, i) => (
                <tr key={i}>
                  <td className="font-mono-data text-xs">{p.codigo}</td>
                  <td>{p.nome}</td>
                  <td><Badge variant="green">{CATEGORIA_LABEL[p.categoria] || p.categoria}</Badge></td>
                  <td className="font-mono-data text-xs">{p.lote || "—"}</td>
                  <td className="font-mono-data text-xs">{fmtDateOnly(p.data_validade)}</td>
                  <td className="text-right font-mono-data font-bold">{p.estoque_atual}</td>
                  <td>{p.vencido ? <Badge variant="danger">Vencido</Badge> : <Badge variant="warn">A vencer</Badge>}</td>
                </tr>
              ))}</tbody>
            </table></div>}
        </Card>
      </div>
    </div>
  );
}

function Compras({ data, canSee }) {
  const monthly = data.monthly || [], sup = data.bySupplier || [], com = data.byCommunity || [];
  const maxSup = Math.max(1, ...sup.map((s) => s.total_valor));
  const maxCom = Math.max(1, ...com.map((s) => s.total_valor));
  const maxMon = Math.max(1, ...monthly.map((m) => m.total));
  return (
    <div className="space-y-6">
      <Card title="Compras por Mês" icon={CalendarRange}>
        {monthly.length === 0 ? <div className="text-center text-[#777] font-mono-data text-sm py-8">Sem compras ainda.</div>
          : <div className="space-y-3">
            {monthly.map((m, i) => (
              <div key={i}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-mono-data font-bold">{m.mes}</span>
                  <div className="flex items-center gap-3 text-xs font-mono-data">
                    <span className="text-[#777]">Fornec: {fmtBRL(m.fornecedor)}</span>
                    <span className="text-[#777]">Coop: {fmtBRL(m.comunidade)}</span>
                    <span className="font-bold text-[#111]">{fmtBRL(m.total)}</span>
                  </div>
                </div>
                <Bar value={m.total} max={maxMon} />
              </div>
            ))}
          </div>}
      </Card>
      {canSee("almoxarife", "presidencia") && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Compras por Fornecedor" icon={Building2}>
            {sup.length === 0 ? <div className="text-center text-[#777] font-mono-data text-sm py-8">Sem compras.</div>
              : <div className="space-y-3">{sup.map((s, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <div className="min-w-0">
                      <span className="font-medium truncate">{s.fornecedor}</span>
                      {s.cnpj && <span className="font-mono-data text-xs text-[#777] ml-2">{s.cnpj}</span>}
                    </div>
                    <div className="flex items-center gap-3"><span className="font-mono-data text-xs text-[#555]">{s.qtd_compras}x</span><span className="font-mono-data font-bold">{fmtBRL(s.total_valor)}</span></div>
                  </div>
                  <Bar value={s.total_valor} max={maxSup} color="#3D6B57" />
                </div>
              ))}</div>}
          </Card>
          <Card title="Compras por Comunidade" icon={Trees} color="#2D6A4F">
            {com.length === 0 ? <div className="text-center text-[#777] font-mono-data text-sm py-8">Sem compras.</div>
              : <div className="space-y-3">{com.map((c, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium">{c.comunidade}</span>
                    <div className="flex items-center gap-3"><span className="font-mono-data text-xs text-[#555]">{c.qtd_compras}x</span><span className="font-mono-data font-bold">{fmtBRL(c.total_valor)}</span></div>
                  </div>
                  <Bar value={c.total_valor} max={maxCom} color="#2D6A4F" />
                </div>
              ))}</div>}
          </Card>
        </div>
      )}
    </div>
  );
}

function Epis({ data }) {
  const rows = data.epiByCollab || [];
  const all = data.epiAll || [];
  const max = Math.max(1, ...rows.map((r) => r.total_itens));
  return (
    <div className="space-y-6">
      <Card title="EPIs Entregues por Colaborador" icon={HardHat}>
        {rows.length === 0 ? <div className="text-center text-[#777] font-mono-data text-sm py-8">Nenhuma entrega registrada.</div>
          : <div className="overflow-x-auto"><table className="brutal-table">
            <thead><tr><th>Colaborador</th><th>Setor</th><th className="text-right">Entregas</th><th className="text-right">Total Itens</th><th>Distribuição</th><th>Ficha</th></tr></thead>
            <tbody>{rows.map((r, i) => (
              <tr key={i}>
                <td className="font-medium">{r.nome}</td>
                <td className="text-sm text-[#555]">{r.setor || "—"}</td>
                <td className="text-right font-mono-data">{r.qtd_entregas}</td>
                <td className="text-right font-mono-data font-bold">{r.total_itens}</td>
                <td className="w-40"><Bar value={r.total_itens} max={max} /></td>
                <td><a href={`/imprimir/colaborador/${r.collaborator_id}`} target="_blank" rel="noreferrer" className="font-mono-data text-xs uppercase tracking-widest text-[#FF4F00] hover:underline">Imprimir →</a></td>
              </tr>
            ))}</tbody>
          </table></div>}
      </Card>
      <Card title="Histórico Individual de CA por Entrega" icon={HardHat} color="#3D6B57">
        {all.length === 0 ? <div className="text-center text-[#777] font-mono-data text-sm py-8">Sem entregas registradas.</div>
          : <div className="overflow-x-auto max-h-[480px] overflow-y-auto"><table className="brutal-table">
            <thead><tr><th>Data</th><th>Recebedor</th><th>EPI</th><th>CA Produto</th><th>CA Individual</th><th className="text-right">Qtd</th><th>Status</th></tr></thead>
            <tbody>{all.map((e) => (
              <tr key={e.id}>
                <td className="font-mono-data text-xs">{fmtDate(e.data_entrega)}</td>
                <td className="font-medium">{e.collaborator_nome}</td>
                <td className="text-sm">{e.product_nome}</td>
                <td className="font-mono-data text-xs">{e.product_ca || "—"}</td>
                <td className="font-mono-data text-xs font-bold">{e.ca_individual || e.product_ca || "—"}</td>
                <td className="text-right font-mono-data">{e.quantidade}</td>
                <td><Badge variant={e.status === "devolvido" ? "default" : "success"}>{e.status}</Badge></td>
              </tr>
            ))}</tbody>
          </table></div>}
      </Card>
    </div>
  );
}

function Consumo({ data }) {
  const rows = data.bySector || [];
  const max = Math.max(1, ...rows.map((r) => r.total_itens));
  return (
    <Card title="Consumo por Setor (Requisições Entregues)" icon={Network}>
      {rows.length === 0 ? <div className="text-center text-[#777] font-mono-data text-sm py-8">Nenhuma requisição entregue ainda.</div>
        : <div className="space-y-3">{rows.map((r, i) => (
          <div key={i}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="font-medium">{r.setor}</span>
              <div className="flex items-center gap-3"><span className="font-mono-data text-xs text-[#555]">{r.qtd_requisicoes} req.</span><span className="font-mono-data font-bold">{r.total_itens} itens</span></div>
            </div>
            <Bar value={r.total_itens} max={max} color="#2D6A4F" />
          </div>
        ))}</div>}
    </Card>
  );
}



function CAEpi({ data }) {
  if (!data) return <div className="text-[#777] font-mono-data text-sm">Carregando...</div>;
  const { vencidos = [], a_vencer = [], ok = [], total = 0 } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase text-[#555]">Total de EPIs</div><div className="font-display font-black text-2xl">{total}</div></div>
        <div className="brutal-card-sm p-3 !bg-[#E60000] !text-white"><div className="font-mono-data text-[10px] uppercase opacity-80">CA Vencido</div><div className="font-display font-black text-2xl">{vencidos.length}</div></div>
        <div className="brutal-card-sm p-3 !bg-[#FFC107] !text-[#111]"><div className="font-mono-data text-[10px] uppercase">A Vencer (30d)</div><div className="font-display font-black text-2xl">{a_vencer.length}</div></div>
        <div className="brutal-card-sm p-3 !bg-[#1A7F37] !text-white"><div className="font-mono-data text-[10px] uppercase opacity-80">CA OK / sem CA</div><div className="font-display font-black text-2xl">{ok.length}</div></div>
      </div>

      {vencidos.length > 0 && (
        <Card title={`EPIs com CA VENCIDO (${vencidos.length})`} icon={AlertOctagon} color="#E60000">
          <CaList rows={vencidos} variant="danger" />
        </Card>
      )}
      {a_vencer.length > 0 && (
        <Card title={`EPIs com CA a vencer em até 30 dias (${a_vencer.length})`} icon={AlertOctagon} color="#FFC107">
          <CaList rows={a_vencer} variant="warn" />
        </Card>
      )}
      {ok.length > 0 && (
        <Card title={`EPIs com CA válido / sem CA (${ok.length})`} icon={HardHat}>
          <CaList rows={ok} variant="success" />
        </Card>
      )}
    </div>
  );
}

function CaList({ rows, variant }) {
  return (
    <div className="overflow-x-auto"><table className="brutal-table">
      <thead><tr><th>Código</th><th>EPI</th><th>CA</th><th>Validade CA</th><th>Dias</th><th>Fabricante</th><th>Lote</th><th className="text-right">Estoque</th></tr></thead>
      <tbody>{rows.map((p, i) => (
        <tr key={i}>
          <td className="font-mono-data font-bold">{p.codigo}</td>
          <td>{p.nome}</td>
          <td className="font-mono-data text-xs"><b>{p.ca || "—"}</b></td>
          <td className="font-mono-data text-xs">{p.ca_validade ? new Date(p.ca_validade).toLocaleDateString("pt-BR") : "—"}</td>
          <td className="font-mono-data text-xs"><Badge variant={variant}>{p.dias_restantes === null ? "—" : (p.dias_restantes >= 0 ? `${p.dias_restantes}d` : `−${Math.abs(p.dias_restantes)}d`)}</Badge></td>
          <td className="text-xs">{p.fabricante || "—"}</td>
          <td className="font-mono-data text-xs">{p.lote || "—"}</td>
          <td className="text-right font-mono-data font-bold">{p.estoque_atual}</td>
        </tr>
      ))}</tbody>
    </table></div>
  );
}


function Movs({ data, periodStart, periodEnd, setStart, setEnd, reload }) {
  const r = data.movPeriod;
  return (
    <div className="space-y-4">
      <Card title="Movimentações — Entradas e Saídas" icon={ArrowLeftRight}>
        <div className="flex flex-wrap gap-3 items-end mb-4">
          <div>
            <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">De</label>
            <input type="date" value={periodStart} onChange={(e) => setStart(e.target.value)} className="border-2 border-[#111] p-2 text-sm" data-testid="movs-start" />
          </div>
          <div>
            <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Até</label>
            <input type="date" value={periodEnd} onChange={(e) => setEnd(e.target.value)} className="border-2 border-[#111] p-2 text-sm" data-testid="movs-end" />
          </div>
          <button onClick={reload} className="brutal-btn bg-[#FF4F00] text-white px-4 py-2 font-display font-bold uppercase text-xs tracking-widest" data-testid="movs-apply">Aplicar</button>
          <ExportButtons report="movements" params={{ start: periodStart, end: periodEnd }} />
        </div>
        {r && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Total Movs</div><div className="font-display font-black text-2xl">{r.total}</div></div>
              <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#1A7F37]">Entradas</div><div className="font-display font-black text-2xl">{r.entrada_qtd}</div><div className="font-mono-data text-xs text-[#1A7F37]">{fmtBRL(r.entrada_valor)}</div></div>
              <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#FF4F00]">Saídas</div><div className="font-display font-black text-2xl">{r.saida_qtd}</div><div className="font-mono-data text-xs text-[#FF4F00]">{fmtBRL(r.saida_valor)}</div></div>
              <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#3D6B57]">Saldo Valor</div><div className={`font-display font-black text-2xl ${(r.saldo_valor || 0) >= 0 ? "text-[#1A7F37]" : "text-[#E60000]"}`}>{fmtBRL(r.saldo_valor)}</div></div>
              <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Exibindo</div><div className="font-display font-black text-2xl">{(r.movimentos || []).length}</div><div className="font-mono-data text-[10px] text-[#777]">últimas</div></div>
            </div>
            <div className="overflow-x-auto"><table className="brutal-table" data-testid="movs-table">
              <thead><tr><th>Data</th><th>Tipo</th><th>Código</th><th>Produto</th><th className="text-right">Qtd</th><th className="text-right">V.Unit</th><th className="text-right">V.Total</th><th>Motivo</th><th>Responsável</th></tr></thead>
              <tbody>{(r.movimentos || []).map((m) => (
                <tr key={m.id}>
                  <td className="font-mono-data text-xs">{fmtDate(m.created_at)}</td>
                  <td>{m.tipo === "entrada" ? <Badge variant="success">↑ Entrada</Badge> : <Badge variant="orange">↓ Saída</Badge>}</td>
                  <td className="font-mono-data text-xs">{m.product_codigo}</td>
                  <td>{m.product_nome}</td>
                  <td className="text-right font-mono-data">{m.quantidade}</td>
                  <td className="text-right font-mono-data text-xs">{fmtBRL(m.valor_unitario)}</td>
                  <td className="text-right font-mono-data font-bold">{fmtBRL(m.valor_total)}</td>
                  <td className="text-sm text-[#555]">{m.motivo || "—"}</td>
                  <td className="text-xs text-[#555]">{m.responsavel_nome || "—"}</td>
                </tr>
              ))}</tbody>
            </table></div>
          </>
        )}
      </Card>
    </div>
  );
}

function EstoqueAtual({ data, onlyLow, setOnlyLow, reload }) {
  if (!data) return <div className="text-[#777] font-mono-data text-sm">Carregando...</div>;
  return (
    <div className="space-y-4">
      <Card title="Estoque Atual" icon={Package}>
        <div className="flex flex-wrap gap-3 items-center mb-4">
          <label className="flex items-center gap-2 text-sm font-mono-data uppercase tracking-widest text-[#555]" data-testid="stock-only-low-label">
            <input type="checkbox" checked={onlyLow} onChange={(e) => { setOnlyLow(e.target.checked); setTimeout(reload, 0); }} data-testid="stock-only-low" className="accent-[#FF4F00]" />
            Só mostrar estoque baixo/zerado
          </label>
          <ExportButtons report="products" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Produtos</div><div className="font-display font-black text-2xl">{data.total_produtos}</div></div>
          <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Itens totais</div><div className="font-display font-black text-2xl">{data.total_itens}</div></div>
          <div className="brutal-card-sm p-3 !bg-[#3D6B57] !text-white"><div className="font-mono-data text-[10px] uppercase opacity-80">Valor em Estoque</div><div className="font-display font-black text-2xl">{fmtBRL(data.total_valor)}</div></div>
          <div className="brutal-card-sm p-3 !bg-[#FFC107] !text-[#111]"><div className="font-mono-data text-[10px] uppercase">Baixo / Zerado</div><div className="font-display font-black text-2xl">{data.baixo} / {data.zerado}</div></div>
        </div>
        <div className="overflow-x-auto"><table className="brutal-table" data-testid="stock-table">
          <thead><tr><th>Código</th><th>Produto</th><th>Cat.</th><th>Unid</th><th className="text-right">Estoque</th><th className="text-right">Mín</th><th className="text-right">Preço</th><th className="text-right">Valor</th><th>Status</th></tr></thead>
          <tbody>{data.rows.map((p) => (
            <tr key={p.id} data-testid={`stock-row-${p.id}`}>
              <td className="font-mono-data font-bold text-xs">{p.codigo}</td>
              <td>{p.nome}</td>
              <td className="text-xs">{CATEGORIA_LABEL[p.categoria] || p.categoria}</td>
              <td className="font-mono-data text-xs">{p.unidade}</td>
              <td className="text-right font-mono-data font-bold">{p.estoque_atual}</td>
              <td className="text-right font-mono-data text-xs text-[#777]">{p.estoque_minimo}</td>
              <td className="text-right font-mono-data text-xs">{fmtBRL(p.preco_custo)}</td>
              <td className="text-right font-mono-data font-bold">{fmtBRL(p.valor_total)}</td>
              <td>{p.status === "zerado" ? <Badge variant="danger">Zerado</Badge> : p.status === "baixo" ? <Badge variant="warn">Baixo</Badge> : <Badge variant="success">OK</Badge>}</td>
            </tr>
          ))}</tbody>
        </table></div>
      </Card>
    </div>
  );
}

function ResumoMensal({ data }) {
  if (!data) return <div className="text-[#777] font-mono-data text-sm">Carregando...</div>;
  const maxSaldo = Math.max(1, ...data.map((r) => Math.max(Math.abs(r.saldo_valor || 0), r.entrada_valor || 0, r.saida_valor || 0)));
  return (
    <div className="space-y-4">
      <Card title="Resumo Mensal (últimos 12 meses)" icon={CalendarRange}>
        <div className="overflow-x-auto"><table className="brutal-table" data-testid="monthly-table">
          <thead><tr>
            <th>Mês</th>
            <th className="text-right">Entradas (qtd)</th>
            <th className="text-right">Saídas (qtd)</th>
            <th className="text-right">Entrada R$</th>
            <th className="text-right">Saída R$</th>
            <th className="text-right">Saldo R$</th>
            <th className="text-right">Reqs</th>
            <th className="text-right">C. Forn.</th>
            <th className="text-right">C. Comun.</th>
          </tr></thead>
          <tbody>{data.map((r) => (
            <tr key={r.mes_label} data-testid={`month-${r.mes_label}`}>
              <td className="font-mono-data font-bold">{r.mes_label}</td>
              <td className="text-right font-mono-data">{r.entrada_qtd}</td>
              <td className="text-right font-mono-data">{r.saida_qtd}</td>
              <td className="text-right font-mono-data text-[#1A7F37]">{fmtBRL(r.entrada_valor)}</td>
              <td className="text-right font-mono-data text-[#FF4F00]">{fmtBRL(r.saida_valor)}</td>
              <td className={`text-right font-mono-data font-bold ${(r.saldo_valor || 0) >= 0 ? "text-[#1A7F37]" : "text-[#E60000]"}`}>{fmtBRL(r.saldo_valor)}</td>
              <td className="text-right font-mono-data">{r.requisicoes}</td>
              <td className="text-right font-mono-data">{r.compras_fornecedor} <span className="text-[10px] text-[#777]">{fmtBRL(r.compras_fornecedor_valor)}</span></td>
              <td className="text-right font-mono-data">{r.compras_comunidade} <span className="text-[10px] text-[#777]">{fmtBRL(r.compras_comunidade_valor)}</span></td>
            </tr>
          ))}</tbody>
        </table></div>
        <div className="mt-6 space-y-2">
          <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-2">Saldo mensal (visual)</div>
          {data.map((r) => (
            <div key={r.mes_label} className="flex items-center gap-2">
              <span className="font-mono-data text-xs w-16 text-[#555]">{r.mes_label}</span>
              <div className="flex-1"><Bar value={Math.abs(r.saldo_valor || 0)} max={maxSaldo} color={(r.saldo_valor || 0) >= 0 ? "#1A7F37" : "#E60000"} /></div>
              <span className={`font-mono-data text-xs font-bold w-24 text-right ${(r.saldo_valor || 0) >= 0 ? "text-[#1A7F37]" : "text-[#E60000]"}`}>{fmtBRL(r.saldo_valor)}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function RequisicoesReport({ data, periodStart, periodEnd, setStart, setEnd, reqStatus, setReqStatus, reqCategoria, setReqCategoria, reload }) {
  return (
    <div className="space-y-4">
      <Card title="Requisições" icon={Network}>
        <div className="flex flex-wrap gap-3 items-end mb-4">
          <div>
            <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">De</label>
            <input type="date" value={periodStart} onChange={(e) => setStart(e.target.value)} className="border-2 border-[#111] p-2 text-sm" data-testid="reqs-start" />
          </div>
          <div>
            <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Até</label>
            <input type="date" value={periodEnd} onChange={(e) => setEnd(e.target.value)} className="border-2 border-[#111] p-2 text-sm" data-testid="reqs-end" />
          </div>
          <div>
            <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Status</label>
            <select value={reqStatus} onChange={(e) => setReqStatus(e.target.value)} className="border-2 border-[#111] p-2 text-sm bg-white" data-testid="reqs-status">
              <option value="">Todos</option>
              <option value="pendente">Pendente</option>
              <option value="aprovada">Aprovada</option>
              <option value="rejeitada">Rejeitada</option>
              <option value="entregue">Entregue</option>
              <option value="devolvida">Devolvida</option>
              <option value="cancelada">Cancelada</option>
            </select>
          </div>
          <div>
            <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Categoria</label>
            <select value={reqCategoria} onChange={(e) => setReqCategoria(e.target.value)} className="border-2 border-[#111] p-2 text-sm bg-white" data-testid="reqs-categoria">
              <option value="">Todas</option>
              <option value="SEDE">SEDE</option>
              <option value="MATERIAL">MATERIAL</option>
              <option value="ALIMENTACAO">ALIMENTAÇÃO</option>
              <option value="OUTROS">OUTROS</option>
            </select>
          </div>
          <button onClick={reload} className="brutal-btn bg-[#FF4F00] text-white px-4 py-2 font-display font-bold uppercase text-xs tracking-widest" data-testid="reqs-apply">Aplicar</button>
          <ExportButtons report="requisitions" />
        </div>
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Total</div><div className="font-display font-black text-2xl">{data.total}</div></div>
              <div className="brutal-card-sm p-3 !bg-[#3D6B57] !text-white"><div className="font-mono-data text-[10px] uppercase opacity-80">Valor Total</div><div className="font-display font-black text-2xl">{fmtBRL(data.total_valor)}</div></div>
              <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#FFC107]">Pendentes</div><div className="font-display font-black text-2xl">{data.by_status.pendente || 0}</div></div>
              <div className="brutal-card-sm p-3"><div className="font-mono-data text-[10px] uppercase tracking-widest text-[#1A7F37]">Entregues</div><div className="font-display font-black text-2xl">{data.by_status.entregue || 0}</div></div>
            </div>
            <div className="overflow-x-auto"><table className="brutal-table" data-testid="reqs-table">
              <thead><tr><th>Nº</th><th>Data</th><th>Solicitante</th><th>Setor</th><th>Categoria</th><th className="text-right">Itens</th><th className="text-right">Valor</th><th>Status</th></tr></thead>
              <tbody>{(data.rows || []).map((r) => (
                <tr key={r.id} data-testid={`reqs-row-${r.id}`}>
                  <td className="font-mono-data font-bold text-xs">{r.numero_sequencial || r.numero}</td>
                  <td className="font-mono-data text-xs">{fmtDate(r.created_at)}</td>
                  <td className="text-sm">{r.requester_nome}</td>
                  <td className="text-xs text-[#555]">{r.sector_nome || "—"}</td>
                  <td className="font-mono-data text-xs">{r.categoria || "—"}</td>
                  <td className="text-right font-mono-data">{(r.itens || []).length}</td>
                  <td className="text-right font-mono-data font-bold">{fmtBRL(r.valor_total_calc)}</td>
                  <td>
                    {r.status === "pendente" && <Badge variant="warn">Pendente</Badge>}
                    {r.status === "aprovada" && <Badge variant="success">Aprovada</Badge>}
                    {r.status === "rejeitada" && <Badge variant="danger">Rejeitada</Badge>}
                    {r.status === "entregue" && <Badge variant="success">Entregue</Badge>}
                    {r.status === "devolvida" && <Badge variant="default">Devolvida</Badge>}
                    {r.status === "cancelada" && <Badge variant="default">Cancelada</Badge>}
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          </>
        )}
      </Card>
    </div>
  );
}
